import type { BrowserOptions } from "@sentry/react";

const MAX_FAILURES = 8;
const CORRELATION_MS = 5000;

function assetPath(value: string | undefined) {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname.length > 200)
      return undefined;
    return /^\/assets\/[A-Za-z0-9_.-]+-[A-Za-z0-9_-]{6,}\.(?:js|css)$/.test(
      url.pathname
    )
      ? url.pathname
      : undefined;
  } catch {
    return undefined;
  }
}

function reportedAsset(error: object) {
  if (!("message" in error) || typeof error.message !== "string")
    return undefined;
  const candidates =
    error.message
      .slice(0, 2000)
      .match(/https?:\/\/[^\s"'<>]+|\/assets\/[A-Za-z0-9_.-]+\.(?:js|css)/g) ??
    [];
  return candidates.map(assetPath).find((path) => path !== undefined);
}

/** Enrich existing Vite import failures without cancelling, retrying or recapturing them. */
export function installModuleLoadingDiagnostics() {
  let active = true;
  const recentFailures: { path: string; at: number }[] = [];
  function context(error: object) {
    const now = performance.now();
    const reportedAssetPath = reportedAsset(error);
    const recentFailedPreloads = [
      ...new Set(
        recentFailures
          .filter(({ at }) => now - at >= 0 && now - at <= CORRELATION_MS)
          .map(({ path }) => path)
      ),
    ];
    return {
      diagnosticVersion: 1,
      trigger: "vite:preloadError",
      urlEvidence: reportedAssetPath
        ? "error-message"
        : recentFailedPreloads.length
          ? "recent-resource-errors"
          : "unavailable",
      reportedAssetPath,
      // These are time-correlated candidates, not proof of the rejected import's URL.
      recentFailedPreloads,
      entryAssetPath: assetPath(
        document.querySelector<HTMLScriptElement>('script[type="module"][src]')
          ?.src
      ),
      online: navigator.onLine,
      visibility: document.visibilityState,
    };
  }
  const failures = new WeakMap<object, ReturnType<typeof context>>();
  function onResourceError(event: Event) {
    try {
      const link = event.target;
      if (
        !(link instanceof HTMLLinkElement) ||
        !(
          link.relList.contains("modulepreload") ||
          link.relList.contains("stylesheet")
        )
      )
        return;
      const path = assetPath(link.href);
      if (!path) return;
      recentFailures.push({ path, at: performance.now() });
      if (recentFailures.length > MAX_FAILURES) recentFailures.shift();
    } catch {
      // Observability must not create another browser error.
    }
  }
  function onPreloadError(event: Event) {
    try {
      if (
        !("payload" in event) ||
        typeof event.payload !== "object" ||
        event.payload === null
      )
        return;
      failures.set(event.payload, context(event.payload));
    } catch {
      // Let Vite propagate its original error even if diagnostics are unavailable.
    }
  }
  window.addEventListener("error", onResourceError, true);
  window.addEventListener("vite:preloadError", onPreloadError);

  const beforeSend: NonNullable<BrowserOptions["beforeSend"]> = (
    event,
    hint
  ) => {
    const error = hint.originalException;
    if (!active || typeof error !== "object" || error === null) return event;
    const details = failures.get(error);
    if (!details) return event;
    failures.delete(error);
    return {
      ...event,
      contexts: { ...event.contexts, module_loading: details },
    };
  };
  return {
    beforeSend,
    dispose() {
      active = false;
      window.removeEventListener("error", onResourceError, true);
      window.removeEventListener("vite:preloadError", onPreloadError);
      recentFailures.length = 0;
    },
  };
}
